import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { ServiceWorkerUpdateService } from '@libs/shared/services/service-worker-update.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthService } from '@sprint-expert-manager/app/core/auth/auth.service';
import { ChannelIoService } from '@sprint-expert-manager/app/core/script/channel-io/channel-io.service';
import { SseService } from '@sprint-expert-manager/app/core/sse/sse.service';
import { injectCoreFeature } from '@sprint-expert-manager/app/core/state/core.service';

import { environment } from '@sprint-expert-manager/environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-root',
  template: ` <router-outlet /> `,
  styles: [
    `
      :host {
        display: flex;
        flex: 1 1 auto;
        width: 100%;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  readonly core = injectCoreFeature();
  readonly auth = inject(AuthService);
  readonly router = inject(Router);
  readonly channelIo = inject(ChannelIoService);
  readonly sse = inject(SseService);
  readonly serviceWorkerUpdateService = inject(ServiceWorkerUpdateService);

  isLoaded = false;
  isScriptLoaded = false;
  isSseOpened = false;
  isNativePlatform = Capacitor.isNativePlatform();

  constructor() {
    document.title = environment.title;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        if (this.auth.isAuthenticated$$()) {
          if (!this.auth.hasValidAccessToken()) {
            this.auth.refreshToken();
          }
        }
      }
    });

    if (Capacitor.isNativePlatform()) {
      ScreenOrientation.lock({ orientation: 'portrait' }).then();
    }
  }

  ngOnInit(): void {
    this.addS3PreconnectToHtmlHead();
    this.initStatusBar();

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated && !this.isLoaded) {
        this.bootstrap();
      }
      if (!isAuthenticated) {
        this.shutdown();
      }
    });
  }

  ngOnDestroy(): void {
    this.closeSse();
    this.closeChannelIo();
  }

  private addS3PreconnectToHtmlHead() {
    const link: HTMLLinkElement = document.createElement('link');
    link.rel = 'preconnect';
    link.href = environment.s3Path;
    document.head.appendChild(link);
  }

  private bootstrap(): void {
    this.openSse();
    this.core.fetchAppStatus();

    this.isLoaded = true;
  }

  private shutdown() {
    this.closeSse();
    this.closeChannelIo();
    this.isLoaded = false;
    this.navigateToLoginPage();
  }

  private openSse(): void {
    if (!this.isSseOpened) {
      this.sse.buildConnection();
      this.isSseOpened = true;
    }
  }

  // private openChannelIo(user: UserRecord, expert: ExpertRecord): void {
  //   if (!this.isScriptLoaded && user && expert) {
  //     this.channelIo.loadScript();
  //     this.channelIo.boot(user, expert);
  //     this.isScriptLoaded = true;
  //   }
  // }

  private closeSse(): void {
    if (this.isSseOpened) {
      this.sse.closeConnection();
    }
  }

  private closeChannelIo(): void {
    if (this.isScriptLoaded) {
      this.channelIo.shutdown();
    }
  }

  private navigateToLoginPage() {
    this.router.navigateByUrl('/home').then();
  }

  private initStatusBar() {
    if (!this.isNativePlatform) return;
    // if (this.core.webColorScheme$$() === 'Dark') {
    //   StatusBar.setStyle({ style: Style.Dark }).then();
    // } else if (this.core.webColorScheme$$() === 'Light') {
    //   StatusBar.setStyle({ style: Style.Light }).then();
    // } else {
    //   StatusBar.setStyle({ style: Style.Default }).then();
    // }
  }
}
